import { memo, useState, useEffect } from 'react';
import { SimpleTreeView, useSimpleTreeViewApiRef } from '@mui/x-tree-view';
import TreeItemGroupComponent from './TreeItemGroupComponent';
import AutoCompleteControl from './InputComponents/AutoCompleteControl';
import {
  getSelectedCodeNames,
  handleSelectionOfMultiSelectOnChange,
} from '../store/Helpers/LandingHelper';
import { IconButton, TextField, Tooltip } from '@mui/material';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { useTranslation } from 'react-i18next';
import type { IFilterOption, ISimpleTreeProps, ITreeExand, ISimpleTreeNode } from '../../types';

/**
 * This component responsible for creating filter tree based on the provided schema
 * @props {filterTreeProps} is list of props required for the simple tree
 * @callback {handleChanges} is callback function, called on change of filter item
 * @returns {JSX.Element} a SimpleTreeView component or span element.
 */
function SimpleTreeViewComponent({
  filterTreeProps,
  handleChanges,
}: Readonly<{
  filterTreeProps: ISimpleTreeProps;
  handleChanges: (selectedCodes: IFilterOption, type: string) => void;
}>) {
  const { treeNodes, availableFilterCodes, initialExpandValue, filterDataAvailable } =
    filterTreeProps;
  let { filteredCodes } = filterTreeProps;

  const apiRef = useSimpleTreeViewApiRef();
  const { t } = useTranslation();
  const [expandedTree, setExpandedTree] = useState<ITreeExand>(initialExpandValue);
  const [itemNameForShow, setItemNameForShow] = useState<IFilterOption>(filteredCodes);

  // Update itemNameForShow when filteredCodes changes from parent
  useEffect(() => {
    const updatedNames: IFilterOption = {};
    for (const treeNode of treeNodes) {
      const names = getSelectedCodeNames(treeNode, filteredCodes);
      updatedNames[treeNode.type] = names;
    }
    setItemNameForShow(updatedNames);
  }, [filteredCodes, treeNodes]);

  // clear a specific filter
  const handleClearClick = (type: string): void => {
    const copy: IFilterOption = { ...filteredCodes, [type]: [] };
    filteredCodes = copy;
    handleChanges(copy, type);
  };

  // checkbox selection
  const handleItemChange = (itemIds: string[], treeNode: ISimpleTreeNode): void => {
    const selectedCodes = handleSelectionOfMultiSelectOnChange(
      itemIds,
      treeNode,
      filteredCodes,
      availableFilterCodes
    );

    const nameOfSelectedCodes = getSelectedCodeNames(treeNode, selectedCodes);
    const names = { ...itemNameForShow, [treeNode.type]: nameOfSelectedCodes };
    setItemNameForShow(names);
    handleChanges(selectedCodes, treeNode.type);
  };

  // expand a specific item
  const expandItem = (type: string): void => {
    setExpandedTree((prev) => ({ ...prev, [type]: true }));
  };

  // collapse a specific item
  const collapseItem = (type: string): void => {
    setExpandedTree((prev) => ({ ...prev, [type]: false }));
  };

  return (
    <>
      {filterDataAvailable ? (
        treeNodes.map((treeNode: ISimpleTreeNode) => (
          <SimpleTreeView
            key={treeNode.type}
            multiSelect
            checkboxSelection
            apiRef={apiRef}
            onSelectedItemsChange={(_e, ids) => handleItemChange(ids, treeNode)}
            defaultExpandedItems={[treeNode.expanded]}
            selectedItems={filteredCodes[treeNode.type]}
            onItemExpansionToggle={(_e, _id, isExpanded) =>
              isExpanded ? expandItem(treeNode.type) : collapseItem(treeNode.type)
            }
            className="filter-treeview"
          >
            {treeNode.treeItems.length > 0 && (
              <TreeItemGroupComponent
                key={treeNode.type}
                name={treeNode.label}
                code={treeNode.enumValue}
                treeItems={treeNode.treeItems}
              >
                {filteredCodes[treeNode.type].length > 0 && (
                  <Tooltip title={t('tooltip.clearAll')}>
                    <IconButton
                      aria-label="filter"
                      onClick={() => handleClearClick(treeNode.type)}
                      className="filter-button"
                      data-testid="clearFilterButton"
                    >
                      <FilterAltOffIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </TreeItemGroupComponent>
            )}
            {filteredCodes[treeNode.type].length > 0 && !expandedTree[treeNode.type] && (
              <AutoCompleteControl
                value={itemNameForShow[treeNode.type]}
                options={itemNameForShow[treeNode.type]}
                disabled
                id={treeNode.type}
                multiple
                freeSolo={false}
                clearOnBlur={false}
                renderInput={(params) => <TextField {...params} />}
                className="collapsed"
                handleChange={() => {}} //This is not used in this scope
              />
            )}
          </SimpleTreeView>
        ))
      ) : (
        <span className="noDataAvailable-label">{t('labels.noDataFound')}</span>
      )}
    </>
  );
}

export default memo(SimpleTreeViewComponent);
